/**
 * Monaco YAML completions for Caseblocks case-template schema.
 * Vue 2 / webpack 4 safe — no optional chaining.
 */

var FIELD_TYPES = [
  { label: "string", detail: "Short text" },
  { label: "text", detail: "Longer text" },
  { label: "number", detail: "Integer or float" },
  { label: "boolean", detail: "True / false" },
  { label: "date", detail: "Date or datetime" },
  { label: "relation", detail: "Link to another case type" },
  { label: "entity", detail: "Nested object or table" },
  { label: "document", detail: "File attachment" },
  { label: "select", detail: "Dropdown from options" },
  { label: "email_address", detail: "Email with validation" },
  { label: "password", detail: "Secret; stored hashed" },
  { label: "address", detail: "Address with geocoding" },
  { label: "location", detail: "Map coordinates" },
  { label: "multi_value", detail: "Multiple values" },
  { label: "case_type_states", detail: "Case status picker" },
  { label: "rich_text", detail: "Formatted text" }
]

var ROOT_KEYS = [
  { label: "name", detail: "Schema / template name" },
  { label: "fieldsets", detail: "Groups of fields" },
  { label: "events", detail: "Lifecycle script bindings" },
  { label: "case_data_views", detail: "Which fieldsets appear on new / edit / detail" }
]

var FIELDSET_KEYS = [
  { label: "name", detail: "Fieldset key (required)" },
  { label: "display_name", detail: "Label in the UI" },
  { label: "display", detail: "Display option" },
  { label: "display_toggle_field", detail: "Field that shows/hides this fieldset" },
  { label: "fields", detail: "Fields in this fieldset" }
]

var FIELD_KEYS = [
  { label: "name", detail: "Field key (required)" },
  { label: "type", detail: "Field type (required)" },
  { label: "display_name", detail: "Label in the UI" },
  { label: "guidance", detail: "Help text" },
  { label: "required", detail: "Must be filled" },
  { label: "readonly", detail: "Not editable" },
  { label: "hidden", detail: "Hidden from the UI" },
  { label: "default_value", detail: "Default value" },
  { label: "sequence", detail: "Auto-incrementing identifier" },
  { label: "relation", detail: "Related case type code" },
  { label: "uif", detail: "Unique identifier field on the related case (usually *_ref)" },
  { label: "options", detail: "Select options" },
  { label: "cardinality", detail: "oneToOne, oneToMany, …" },
  { label: "fields", detail: "Nested fields (entity)" },
  { label: "item_type", detail: "Nested item type name" },
  { label: "format", detail: "Display format" },
  { label: "validation", detail: "Validation rule / regex" },
  { label: "validation_message", detail: "Shown when validation fails" },
  { label: "precision", detail: "Decimal places (number)" },
  { label: "symbol", detail: "Currency or unit symbol" },
  { label: "internal", detail: "Internal-only field" },
  { label: "display_fields", detail: "Related fields to show" },
  { label: "cascading_parent_field", detail: "Parent field for cascading options" },
  { label: "dependent_fields", detail: "Fields that depend on this one" },
  { label: "time_format", detail: "Time component format" },
  { label: "input_format", detail: "Input date format" },
  { label: "input_timezone", detail: "Input timezone" },
  { label: "export_format", detail: "Export date format" },
  { label: "export_timezone", detail: "Export timezone" }
]

var EVENT_KEYS = [
  { label: "preCreate", detail: "Before create is saved; can fail() or mutate payload" },
  { label: "postCreate", detail: "After create; side effects only" },
  { label: "preSave", detail: "Before update is saved; can fail() or mutate payload" },
  { label: "postSave", detail: "After update; side effects only" },
  { label: "preStateChange", detail: "Before status change" },
  { label: "postStateChange", detail: "After status change" },
  { label: "preDelete", detail: "Before delete" },
  { label: "postDelete", detail: "After delete" }
]

var VIEW_KEYS = [
  { label: "new", detail: "Create-case layout" },
  { label: "edit", detail: "Edit-case layout" },
  { label: "detail", detail: "Read-only detail layout" }
]

var CARDINALITIES = ["oneToOne", "oneToMany", "manyToOne", "manyToMany", "multiple"]
var BOOLEANS = ["true", "false"]
var STRUCTURAL = {
  fieldsets: true,
  fields: true,
  events: true,
  case_data_views: true,
  columns: true,
  rows: true,
  new: true,
  edit: true,
  detail: true
}

var modelContexts = {}
var providerRegistered = false

function lineIndent(line) {
  var match = String(line || "").match(/^(\s*)/)
  return match ? match[1].length : 0
}

function lineKey(line) {
  var match = String(line || "").match(/^\s*(?:-\s+)?([A-Za-z0-9_]+)\s*:/)
  return match ? match[1] : null
}

function findStructuralParent(lines, index, indent) {
  var i
  var key
  var ind
  for (i = index - 1; i >= 0; i--) {
    ind = lineIndent(lines[i])
    key = lineKey(lines[i])
    if (key && ind < indent && STRUCTURAL[key]) return key
  }
  return null
}

function isSchemaModel(model) {
  if (!model || !model.uri) return false
  var path = model.uri.path || ""
  return path.indexOf("case-schema") !== -1
}

export function setSchemaCompletionContext(model, context) {
  if (!model || !model.uri) return
  modelContexts[model.uri.toString()] = context || {}
}

export function clearSchemaCompletionContext(model) {
  if (!model || !model.uri) return
  delete modelContexts[model.uri.toString()]
}

export function inferSchemaCompletionScope(lines, lineIndex, column) {
  var line = lines[lineIndex] || ""
  var prefix = line.substring(0, column - 1)
  var indent = lineIndent(prefix)
  var valueMatch = prefix.match(/^\s*(?:-\s+)?([A-Za-z0-9_]+)\s*:\s*(.*)$/)
  if (valueMatch) {
    var key = valueMatch[1]
    if (key === "type") return { kind: "field-type" }
    if (key === "relation") return { kind: "relation" }
    if (key === "cardinality") return { kind: "cardinality" }
    if (key === "uif") return { kind: "uif" }
    if (key === "fieldset") return { kind: "fieldset-ref" }
    if (key === "required" || key === "readonly" || key === "hidden" || key === "sequence" || key === "internal") {
      return { kind: "boolean" }
    }
    if (EVENT_KEYS.some(function (item) { return item.label === key })) {
      return { kind: "script-name" }
    }
    return { kind: "value" }
  }
  var parent = findStructuralParent(lines, lineIndex, indent === 0 ? 1 : indent)
  if (!parent) return { kind: "root-key", emptyList: /^\s*-\s*$/.test(prefix) }
  if (parent === "fieldsets") return { kind: "fieldset-key", emptyList: /^\s*-\s*$/.test(prefix) }
  if (parent === "fields") return { kind: "field-key", emptyList: /^\s*-\s*$/.test(prefix) }
  if (parent === "events") return { kind: "event-key" }
  if (parent === "case_data_views") return { kind: "view-key" }
  if (parent === "columns") return { kind: "column-key" }
  if (parent === "rows") return { kind: "row-key" }
  if (parent === "new" || parent === "edit" || parent === "detail") return { kind: "view-body-key" }
  return { kind: "field-key", emptyList: /^\s*-\s*$/.test(prefix) }
}

function snippet(monaco, label, detail, insertText, range) {
  return {
    label: label,
    kind: monaco.languages.CompletionItemKind.Snippet,
    insertText: insertText,
    insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
    detail: detail,
    range: range
  }
}

function itemsFrom(monaco, list, kind, range) {
  return list.map(function (item) {
    var label = typeof item === "string" ? item : item.label
    var detail = typeof item === "string" ? undefined : item.detail
    return {
      label: label,
      kind: kind,
      insertText: label,
      detail: detail,
      range: range
    }
  })
}

function fieldsetNamesFromDoc(lines) {
  var names = []
  var i
  var match
  for (i = 0; i < lines.length; i++) {
    match = String(lines[i] || "").match(/^\s*-\s+name:\s*["']?([A-Za-z0-9_]+)["']?\s*$/)
    if (match) names.push(match[1])
  }
  return names
}

export function buildSchemaSuggestions(monaco, scope, context, range, lines) {
  var ctx = context || {}
  var codes = ctx.caseTypeCodes || []
  var scripts = ctx.scriptNames || []
  var uif = ctx.uniqueIdentifierField || ""
  var suggestions = []
  var kindKey = monaco.languages.CompletionItemKind.Field
  var kindEnum = monaco.languages.CompletionItemKind.Enum
  var kindValue = monaco.languages.CompletionItemKind.Value

  if (scope.kind === "root-key") {
    suggestions = itemsFrom(monaco, ROOT_KEYS, kindKey, range)
    suggestions.push(snippet(
      monaco,
      "fieldset",
      "New fieldset with a string field",
      "fieldsets:\n  - name: ${1:summary}\n    fields:\n      - name: ${2:title}\n        type: string\n        required: true\n",
      range
    ))
  } else if (scope.kind === "fieldset-key") {
    suggestions = itemsFrom(monaco, FIELDSET_KEYS, kindKey, range)
    if (scope.emptyList) {
      suggestions.unshift(snippet(
        monaco,
        "fieldset item",
        "Fieldset list item",
        "name: ${1:summary}\n  fields:\n    - name: ${2:title}\n      type: string",
        range
      ))
    }
  } else if (scope.kind === "field-key") {
    suggestions = itemsFrom(monaco, FIELD_KEYS, kindKey, range)
    if (scope.emptyList) {
      suggestions.unshift(snippet(
        monaco,
        "field item",
        "Field list item",
        "name: ${1:field_name}\n  type: ${2:string}",
        range
      ))
      suggestions.unshift(snippet(
        monaco,
        "relation field",
        "Relation to another case type",
        "name: ${1:related}\n  type: relation\n  relation: ${2:other_type}\n  uif: ${1}_ref",
        range
      ))
    } else {
      suggestions.push(snippet(
        monaco,
        "new field",
        "Insert a field",
        "- name: ${1:field_name}\n  type: ${2:string}",
        range
      ))
      suggestions.push(snippet(
        monaco,
        "relation field",
        "Relation to another case type",
        "- name: ${1:related}\n  type: relation\n  relation: ${2:other_type}\n  uif: ${1}_ref",
        range
      ))
    }
  } else if (scope.kind === "field-type") {
    suggestions = itemsFrom(monaco, FIELD_TYPES, kindEnum, range)
  } else if (scope.kind === "relation") {
    suggestions = itemsFrom(monaco, codes, kindValue, range)
  } else if (scope.kind === "cardinality") {
    suggestions = itemsFrom(monaco, CARDINALITIES, kindEnum, range)
  } else if (scope.kind === "boolean") {
    suggestions = itemsFrom(monaco, BOOLEANS, kindEnum, range)
  } else if (scope.kind === "event-key") {
    suggestions = itemsFrom(monaco, EVENT_KEYS, kindKey, range)
  } else if (scope.kind === "script-name") {
    suggestions = itemsFrom(monaco, scripts, kindValue, range)
  } else if (scope.kind === "view-key") {
    suggestions = itemsFrom(monaco, VIEW_KEYS, kindKey, range)
  } else if (scope.kind === "column-key") {
    suggestions = itemsFrom(monaco, [{ label: "fieldset", detail: "Fieldset to show in this column" }], kindKey, range)
  } else if (scope.kind === "row-key") {
    suggestions = itemsFrom(monaco, [{ label: "columns", detail: "Columns in this row" }], kindKey, range)
  } else if (scope.kind === "view-body-key") {
    suggestions = itemsFrom(monaco, [{ label: "rows", detail: "Layout rows" }], kindKey, range)
  } else if (scope.kind === "fieldset-ref") {
    suggestions = itemsFrom(monaco, fieldsetNamesFromDoc(lines || []), kindValue, range)
  } else if (scope.kind === "uif") {
    var uifs = []
    if (uif) uifs.push(uif)
    codes.forEach(function (code) {
      uifs.push(code + "_ref")
    })
    suggestions = itemsFrom(monaco, uifs, kindValue, range)
  }
  return suggestions
}

export function registerSchemaYamlCompletions(monaco) {
  if (providerRegistered || !monaco) return
  providerRegistered = true
  console.debug("[schema-yaml] completion provider registered")
  monaco.languages.registerCompletionItemProvider("yaml", {
    triggerCharacters: [":", " ", "-", "\n"],
    provideCompletionItems: function (model, position) {
      if (!isSchemaModel(model)) return { suggestions: [] }
      var word = model.getWordUntilPosition(position)
      var range = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endColumn: word.endColumn
      }
      var lines = model.getLinesContent()
      var scope = inferSchemaCompletionScope(lines, position.lineNumber - 1, position.column)
      var context = modelContexts[model.uri.toString()] || {}
      console.debug("[schema-yaml] looking up completions", {
        scope: scope && scope.kind,
        line: position.lineNumber,
        column: position.column
      })
      var suggestions = buildSchemaSuggestions(monaco, scope, context, range, lines)
      console.debug("[schema-yaml] " + suggestions.length + " suggestion(s) for " + (scope && scope.kind))
      return {
        suggestions: suggestions
      }
    }
  })
  monaco.languages.registerHoverProvider("yaml", {
    provideHover: function (model, position) {
      if (!isSchemaModel(model)) return null
      var word = model.getWordAtPosition(position)
      if (!word) return null
      var i
      for (i = 0; i < FIELD_TYPES.length; i++) {
        if (FIELD_TYPES[i].label === word.word) {
          return {
            contents: [
              { value: "**" + FIELD_TYPES[i].label + "**" },
              { value: FIELD_TYPES[i].detail }
            ]
          }
        }
      }
      for (i = 0; i < EVENT_KEYS.length; i++) {
        if (EVENT_KEYS[i].label === word.word) {
          return {
            contents: [
              { value: "**" + EVENT_KEYS[i].label + "**" },
              { value: EVENT_KEYS[i].detail }
            ]
          }
        }
      }
      return null
    }
  })
}
